<script setup lang="ts">
  /**
   * Dumb row renderer. Takes pre-computed display strings + a click
   * handler and that's it — no data fetching, no metadata lookup, no
   * store access. Per-setting SFCs wrap this component with their own
   * logic and pass the strings in.
   */

  import { cn, TooltipHint } from '@gat-ship/vue-components'
  import { IconChevronRight, IconLoading } from '@gat-ship/icons'
  import { Badge } from '@gat-ship/vue-components'

  defineProps<{
    title: string
    description?: string
    privilegeLevel?: 'high'
    valueText: string
    statusText: string
    isLoading?: boolean
  }>()

  defineEmits<{
    (e: 'click'): void
  }>()
</script>

<template>
  <button
    type="button"
    :class="
      cn(
        'focus-visible:ring-ring/40 group flex min-h-16 w-full items-center gap-2 px-4 py-4 text-left transition-colors',
        'hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2',
        $attrs.class as string
      )
    "
    @click="$emit('click')"
  >
    <div class="flex min-w-0 grow flex-col gap-1">
      <div
        class="inline-flex w-fit min-w-0 items-center gap-1 text-[13px] font-medium leading-none"
      >
        {{ title }}<TooltipHint v-if="description" :text="description" />
      </div>
      <div class="text-muted-foreground text-[13px] leading-none">
        {{ valueText }}
      </div>
    </div>
    <div class="flex shrink-0 items-center gap-2">
      <badge
        v-if="privilegeLevel === 'high'"
        class="bg-yellow-200 text-yellow-700 dark:bg-yellow-400/15 dark:text-yellow-400"
      >
        High privilege
      </badge>
      <span class="text-muted-foreground text-[13px] leading-none">
        {{ statusText }}
      </span>
      <span
        class="text-muted-foreground group-hover:text-foreground flex size-5 items-center justify-center"
      >
        <IconLoading v-if="isLoading" class="size-5" :is-loading="true" />
        <icon-chevron-right
          v-else
          class="size-5 transition duration-200 ease-out group-hover:translate-x-0.5"
        />
      </span>
    </div>
  </button>
</template>
